<template>
  <div class="domains-view">
    <div class="header">
      <h2>Admin Sites</h2>
      <div class="header-controls">
        <div class="search-container">
          <div class="search-input-wrapper">
            <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
            </svg>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search sites or users"
              class="search-input"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading">
      Loading domains...
    </div>
    <div v-else-if="error" class="error">
      {{ error }}
    </div>
    <div v-else-if="domains && domains.length === 0" class="no-domains">
      No sites found.
    </div>
    <div v-else-if="filteredDomains && filteredDomains.length === 0 && searchQuery.trim()" class="no-domains">
      No sites match your search.
    </div>
    <div v-else-if="domains && domains.length" class="domains-list">
      <div class="domains-header">
        <div class="domain-item">User</div>
        <div class="domain-item sortable" @click="sortColumn('name')">
          Name
          <span class="sort-indicator" v-if="sortBy === 'name'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
        </div>
        <div class="domain-item desktop-only">URL</div>
        <div class="domain-item desktop-only">Scanned/Total Nodes</div>
        <div class="domain-item desktop-only">Created At</div>
        <div class="domain-item desktop-only sortable" @click="sortColumn('lastScanAt')">
          Last scanned
          <span class="sort-indicator" v-if="sortBy === 'lastScanAt'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
        </div>
        <div class="domain-item">Changes</div>
        <div class="domain-item">AI Review</div>
      </div>
      <div v-for="domain in filteredDomains" :key="domain.id"
           :class="['domain-row', isDefaultScanDate(domain.lastScanAt) ? '' : 'clickable']"
           @click="!isDefaultScanDate(domain.lastScanAt) && navigateToScanResults(domain)"
           :title="isDefaultScanDate(domain.lastScanAt) ? 'No scan data available' : 'Click to view latest changes'"
           :style="isDefaultScanDate(domain.lastScanAt) ? 'cursor: default;' : 'cursor: pointer;'">
        <div class="domain-item">
          <div class="user-info">
            <span class="user-name">{{ domain.userName }}</span>
            <span class="user-email">{{ domain.userEmail }}</span>
          </div>
        </div>
        <div class="domain-item">
          <span class="domain-name">{{ domain.name }}</span>
        </div>
        <div class="domain-item desktop-only">{{ domain.url }}</div>
        <div class="domain-item desktop-only">{{ getNodesDisplay(domain) }}</div>
        <div class="domain-item desktop-only">
          <div class="date-time">
            <div class="date">{{ formatDate(domain.createdAt) }}</div>
            <div class="time">{{ formatTime(domain.createdAt) }}</div>
          </div>
        </div>
        <div class="domain-item desktop-only">
          <div v-if="isDefaultScanDate(domain.lastScanAt)" class="na-status">N/A</div>
          <div v-else class="date-time">
            <div class="date">{{ formatDate(domain.lastScanAt) }}</div>
            <div class="time">{{ formatTime(domain.lastScanAt) }}</div>
          </div>
        </div>
        <div class="domain-item">
          <span v-if="isDefaultScanDate(domain.lastScanAt)" class="na-status">N/A</span>
          <span v-else-if="domain.hasChanges" class="changes-badge has-changes" title="Changes detected">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </span>
          <span v-else class="changes-badge no-changes" title="No changes">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M8 12l2.5 2.5L16 9"/>
            </svg>
          </span>
        </div>
        <div class="domain-item">
          <span v-if="isDefaultScanDate(domain.lastScanAt)" class="na-status">N/A</span>
          <span v-else :class="['ai-review-badge', getAiReviewType(domain)]" :title="getAiReviewLabel(domain)">
            <template v-if="getAiReviewType(domain) === 'safe'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
            </template>
            <template v-else-if="getAiReviewType(domain) === 'suspicious'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </template>
            <template v-else-if="getAiReviewType(domain) === 'malicious'">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
              </svg>
            </template>
            <template v-else>
              <span class="ai-review-pending">—</span>
            </template>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { api } from '../../utils/api'
import { useRouter } from 'vue-router'

export default {
  name: 'AdminSitesView',
  setup() {
    const router = useRouter()
    const users = ref([])
    const domains = ref([])
    const loading = ref(true)
    const error = ref('')
    const searchQuery = ref('')
    const sortBy = ref(null)
    const sortOrder = ref('asc')

    const filteredDomains = computed(() => {
      let filtered = domains.value

      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase()
        filtered = filtered.filter(domain => {
          return (domain.name && domain.name.toLowerCase().includes(query)) ||
                 (domain.url && domain.url.toLowerCase().includes(query)) ||
                 (domain.userName && domain.userName.toLowerCase().includes(query)) ||
                 (domain.userEmail && domain.userEmail.toLowerCase().includes(query))
        })
      }

      if (sortBy.value) {
        filtered = [...filtered].sort((a, b) => {
          let aVal, bVal

          if (sortBy.value === 'name') {
            aVal = a.name?.toLowerCase() || ''
            bVal = b.name?.toLowerCase() || ''
          } else if (sortBy.value === 'lastScanAt') {
            aVal = new Date(a.lastScanAt).getTime()
            bVal = new Date(b.lastScanAt).getTime()
          } else if (sortBy.value === 'status') {
            aVal = a.scanStatus || ''
            bVal = b.scanStatus || ''
          }

          if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1
          if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1
          return 0
        })
      }

      return filtered
    })

    const mapStatusToScanStatus = (status) => {
      // Map API status values to scanStatus values used in template
      if (status === 'clean') return 'success'
      if (status === 'warning') return 'warning'
      if (status === 'critical') return 'critical'
      if (status === 'disabled') return 'disabled'
      // Default to error if status is unknown
      return 'error'
    }

    const getCombinedStatus = (aiStatus, heuristicStatus, aiConfidence, aiReason) => {
      const HIGH_CONFIDENCE_THRESHOLD = 0.7
      const hasHighConfidence = aiConfidence !== undefined && aiConfidence !== null && aiConfidence >= HIGH_CONFIDENCE_THRESHOLD
      const isAISafe = aiStatus === 'safe'
      const isAIUnset = aiStatus === 'unset'
      const isHeuristicSafe = heuristicStatus === 'Safe' || heuristicStatus === 'Most probably safe' || heuristicStatus === 'Probably safe'
      const isAIReasonSafe = aiReason === 'safe'
      
      // Special case: aiStatus === 'unset' && heuristicStatus IN ('Most probably safe', 'Probably safe')
      // Return special status to indicate we should use checkmark2.png
      if (isAIUnset && isHeuristicSafe) {
        return 'success-unset-safe'
      }
      
      // Priority 1: Give more weight to aiReason if it's safe and aiConfidence is high
      if (hasHighConfidence && isAIReasonSafe) {
        return 'success'
      }
      
      // Priority 2: Give more weight to AI status if aiConfidence exists and is high
      if (hasHighConfidence) {
        if (isAISafe) return 'success'
        if (aiStatus === 'suspicious') return 'warning'
        if (aiStatus === 'malicious') return 'critical'
      }
      
      // Critical: AI says malicious OR heuristic says Malicious/Highly suspicious
      if (aiStatus === 'malicious' || 
          heuristicStatus === 'Malicious' || 
          heuristicStatus === 'Highly suspicious') {
        return 'critical'
      }
      
      // Warning: AI says suspicious OR heuristic says Suspicious OR Probably safe (less safe)
      if (aiStatus === 'suspicious' || 
          heuristicStatus === 'Suspicious' || 
          heuristicStatus === 'Probably safe') {
        return 'warning'
      }
      
      // Success: AI says safe AND heuristic says Safe or Most probably safe
      if (isAISafe && isHeuristicSafe) {
        return 'success'
      }
      
      // Default to warning for Unsure or other combinations
      if (heuristicStatus === 'Unsure') {
        return 'warning'
      }
      
      // Fallback: if only one status is available
      if (aiStatus === 'safe') return 'success'
      if (aiStatus === 'suspicious') return 'warning'
      if (aiStatus === 'malicious') return 'critical'
      
      // Default to warning for unknown states
      return 'warning'
    }

    const fetchUsers = async () => {
      try {
        loading.value = true
        error.value = ''
        const data = await api('/admin/users?withDomains=true', {}, router)
        users.value = data?.users || []

        // Flatten domains with user information
        const flattenedDomains = []
        users.value.forEach(user => {
          const userName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
          if (user.domains && user.domains.length > 0) {
            user.domains.forEach(domain => {
              flattenedDomains.push({
                ...domain,
                userName,
                userEmail: user.email,
                userId: user.id,
                // Map status field to scanStatus for template compatibility
                scanStatus: mapStatusToScanStatus(domain.status),
                // Pre-compute combined status for efficient template rendering
                combinedStatus: getCombinedStatus(domain.aiStatus, domain.heuristicStatus, domain.aiConfidence, domain.aiReason),
                // scanResults is already included in the domain object from API
              })
            })
          }
        })

        domains.value = flattenedDomains
      } catch (err) {
        console.error('Failed to fetch users:', err)
        // Use code from error response if available, otherwise use default message
        error.value = err.code || 'Something went wrong please contact support'
      } finally {
        loading.value = false
      }
    }

    const navigateToScanResults = (domain) => {
      router.push({
        name: 'scan-results',
        params: { id: domain.id }
      })
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const month = months[date.getMonth()]
      const day = date.getDate().toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${month} ${day}, ${year}`
    }

    const formatTime = (dateString) => {
      const date = new Date(dateString)
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      const seconds = date.getSeconds().toString().padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    }

    const isDefaultScanDate = (lastScanAt) => {
      const date = new Date(lastScanAt)
      const defaultDate = new Date('0001-01-01T00:00:00Z')
      return date.getTime() === defaultDate.getTime()
    }

    const getNodesDisplay = (domain) => {
      // nodesScanned is included directly in the domain object from the API
      const lastScanned = domain.nodesScanned ?? domain.scanResults?.nodesScanned
      const totalNodes = 80000 // Hardcoded total nodes

      if (lastScanned !== undefined && lastScanned !== null) {
        return `${lastScanned.toLocaleString('de-DE')}/${totalNodes.toLocaleString('de-DE')}`
      } else {
        return `N/A/${totalNodes.toLocaleString('de-DE')}`
      }
    }


    const getAiReviewType = (domain) => {
      const aiStatus = domain.aiStatus
      const hasChanges = domain.hasChanges || false

      if (aiStatus === 'safe') return 'safe'
      if (aiStatus === 'suspicious') return 'suspicious'
      if (aiStatus === 'malicious') return 'malicious'
      if (aiStatus === 'unset' && !hasChanges) return 'safe'

      return 'pending'
    }

    const getAiReviewLabel = (domain) => {
      const type = getAiReviewType(domain)
      if (type === 'safe') return 'Safe'
      if (type === 'suspicious') return 'Suspicious'
      if (type === 'malicious') return 'Malicious'
      return 'Pending review'
    }

    const sortColumn = (column) => {
      if (sortBy.value === column) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortBy.value = column
        sortOrder.value = 'asc'
      }
    }

    onMounted(() => {
      fetchUsers()
    })

    return {
      users,
      domains,
      filteredDomains,
      loading,
      error,
      searchQuery,
      sortBy,
      sortOrder,
      navigateToScanResults,
      formatDate,
      formatTime,
      isDefaultScanDate,
      getNodesDisplay,
      getAiReviewType,
      getAiReviewLabel,
      sortColumn
    }
  }
}
</script>

<style scoped>
.domains-view {
  padding: 20px 0;
  margin: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-container {
  display: flex;
  align-items: center;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 20px;
  height: 20px;
  color: white;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 300px;
  height: 38px;
  padding: 0 12px 0 42px;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 16px;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
  color: var(--text-muted);
}

.loading, .error, .no-domains {
  text-align: center;
  padding: 20px;
  font-size: 1.2em;
  color: var(--text-primary);
}

.error {
  color: var(--error-color);
}

.domains-list {
  background: var(--bg-secondary);
  border-radius: 8px;
  box-shadow: var(--shadow);
  margin-top: 20px;
  border: 1px solid var(--border-color);
}

.domains-header {
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 1fr 1fr 1fr 1fr 1fr;
  padding: 15px 20px;
  background: var(--bg-tertiary);
  border-bottom: 2px solid var(--border-color);
  font-weight: bold;
  color: var(--text-primary);
  font-size: 14px;
}

.domain-row {
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 1fr 1fr 1fr 1fr 1fr;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  color: var(--text-primary);
  transition: background-color 0.2s ease;
  font-size: 14px;
}

.domain-row:last-child {
  border-bottom: none;
}

.domain-row.clickable:hover {
  background-color: rgba(0, 123, 255, 0.1);
  cursor: pointer;
}

.domain-row:not(.clickable):hover {
  background-color: transparent;
  cursor: default;
}

.domain-item {
  padding: 0 10px;
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-name {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}

.user-email {
  font-size: 12px;
  color: var(--text-muted);
}

.domain-name {
  color: white;
  font-weight: 500;
}

.toggle-switch {
  cursor: pointer;
  user-select: none;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.toggle-track {
  position: relative;
  width: 80px;
  height: 24px;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  overflow: hidden;
}

.track-disabled {
  background-color: var(--error-bg);
  border: 1px solid var(--error-color);
}

.track-enabled {
  background-color: var(--success-bg);
  border: 1px solid var(--success-color);
}

.toggle-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  z-index: 1;
}

.text-enabled {
  transition: opacity 0.2s ease;
  position: absolute;
  left: 8px;
  text-align: left;
}

.text-disabled {
  transition: opacity 0.2s ease;
  position: absolute;
  right: 8px;
  text-align: right;
}

.text-enabled.active {
  opacity: 1;
  color: white;
}

.text-disabled.active {
  opacity: 1;
  color: white;
}

.text-enabled:not(.active) {
  opacity: 0;
}

.text-disabled:not(.active) {
  opacity: 0;
}

.toggle-knob {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 20px;
  height: 20px;
  background-color: white;
  border: 1px solid var(--border-color);
  border-radius: 50%;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.knob-enabled {
  transform: translateX(58px);
}

.knob-disabled {
  transform: translateX(0);
}

.toggle-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.toggle-switch:not(.disabled) .knob-enabled .toggle-icon {
  color: var(--success-color);
}

.toggle-switch:not(.disabled) .knob-disabled .toggle-icon {
  color: var(--error-color);
}

.toggle-switch.disabled .toggle-icon {
  color: var(--text-muted);
}

.na-status {
  color: var(--text-muted);
  font-style: italic;
}

.date-time {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  gap: 6px;
}

.date {
  font-weight: 500;
  color: var(--text-primary);
}

.time {
  font-size: 14px;
  color: var(--text-muted);
}

.changes-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.changes-badge svg {
  width: 22px;
  height: 22px;
}

.changes-badge.has-changes {
  color: #f59e0b;
}

.changes-badge.no-changes {
  color: var(--success-color);
}

.changes-badge:hover {
  transform: scale(1.1);
}

.ai-review-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.ai-review-badge svg {
  width: 22px;
  height: 22px;
}

.ai-review-badge.safe {
  color: var(--success-color);
}

.ai-review-badge.suspicious {
  color: #f59e0b;
}

.ai-review-badge.malicious {
  color: #ef4444;
}

.ai-review-badge:hover {
  transform: scale(1.1);
}

.ai-review-pending {
  color: var(--text-muted);
  font-size: 14px;
}

.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;
}

.sortable:hover {
  color: var(--accent-color);
}

.sort-indicator {
  margin-left: 4px;
  font-size: 10px;
  color: var(--accent-color);
}

.desktop-only {
  display: block;
}

@media (max-width: 768px) {
  .domains-view {
    padding: 20px 0;
  }

  .desktop-only {
    display: none !important;
  }

  .header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .header-controls {
    flex-direction: column;
    gap: 12px;
  }

  .search-input {
    width: 100%;
  }

  .domains-header {
    grid-template-columns: 2fr 150px 1fr 1fr;
    font-size: 12px;
    padding: 12px 15px;
  }

  .domain-row {
    grid-template-columns: 2fr 150px 1fr 1fr;
    padding: 12px 15px;
  }

  .user-name {
    font-size: 13px;
  }

  .user-email {
    font-size: 11px;
  }

  .domain-name {
    font-size: 14px;
  }

  .toggle-switch {
    transform: scale(0.8);
  }

  .toggle-text {
    display: none;
  }

  .toggle-track {
    width: 40px;
    height: 20px;
  }

  .toggle-knob {
    width: 16px;
    height: 16px;
  }

  .knob-enabled {
    transform: translateX(22px);
  }

  .toggle-icon {
    transform: scale(0.9);
  }
}
</style>